import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { toastEmailCodeResult } from '../utils/mailFeedback'
import {
  AuthSplitLayout,
  AuthLogo,
  LinkedInIcon,
  WeChatIcon,
} from '../components/Auth/AuthShell'
import LanguageMultiSelect from '../components/Auth/LanguageMultiSelect'
import { RsIcon } from '../components/icons'

const ROLE_ICONS = { GP: 'fund', LP: 'dollar', Startup: 'upgrade', Service: 'settings' }

const SOCIAL_ROLES = [
  { id: 'GP', label: 'GP', desc: 'Fund Manager', backendRole: 'GP' },
  { id: 'LP', label: 'LP', desc: 'Investor', backendRole: 'LP' },
  { id: 'Startup', label: 'Startup', desc: 'Founder', backendRole: 'Startup' },
  { id: 'Service', label: 'Service', desc: 'Advisor / Firm', backendRole: 'ServiceProvider' },
]

function toggleInList(list, item) {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item]
}

export default function CompleteProfile() {
  const navigate = useNavigate()
  const [oauthProvider, setOauthProvider] = useState('')
  const [oauthCode, setOauthCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [verifyCode, setVerifyCode] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [emailError, setEmailError] = useState('')
  const [selectedRoleId, setSelectedRoleId] = useState('GP')
  const [languages, setLanguages] = useState(['en'])
  const [formData, setFormData] = useState({
    realName: '',
    companyShortName: '',
    position: '',
    workEmail: '',
    password: '',
    confirmPassword: '',
  })

  const roleMeta = SOCIAL_ROLES.find((r) => r.id === selectedRoleId) || SOCIAL_ROLES[0]

  useEffect(() => {
    const stored = sessionStorage.getItem('oauth_result')
    if (stored) {
      try {
        const o = JSON.parse(stored)
        setOauthProvider(o.provider || '')
        setOauthCode(o.oauthUid || '')
        if (o.displayName) {
          setFormData((prev) => ({ ...prev, realName: o.displayName }))
        }
        if (o.email) {
          setFormData((prev) => ({ ...prev, workEmail: o.email }))
        }
        sessionStorage.removeItem('oauth_result')
      } catch {
        /* ignore */
      }
    }
    const onMessage = (e) => {
      if (e.data?.type === 'rotationstreet_oauth') {
        setOauthProvider(e.data.provider)
        setOauthCode(e.data.oauthUid)
        if (e.data.displayName) {
          setFormData((prev) => ({ ...prev, realName: e.data.displayName }))
        }
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (field === 'workEmail') setEmailError('')
  }

  const sendVerifyCode = async () => {
    if (!formData.workEmail) {
      toast.error('Please enter your work email')
      return
    }
    try {
      const res = await axios.post('/api/auth/send-email-code', {
        email: formData.workEmail,
        type: 'registration',
      })
      toastEmailCodeResult(res, toast)
      setCountdown(60)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) clearInterval(timer)
          return prev - 1
        })
      }, 1000)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send code')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setEmailError('')
    if (!oauthProvider || !oauthCode) {
      toast.error('Please complete authorization via LinkedIn or WeChat first')
      navigate('/signup')
      return
    }
    if (!formData.realName?.trim()) {
      toast.error('Please enter your full name')
      return
    }
    if (!formData.companyShortName?.trim()) {
      toast.error('Please enter company name')
      return
    }
    if (!formData.position?.trim()) {
      toast.error('Please enter your job title')
      return
    }
    if (!formData.workEmail?.includes('@')) {
      setEmailError('Please enter a valid email address.')
      return
    }
    if (!verifyCode) {
      toast.error('Please enter email verification code')
      return
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    setLoading(true)
    try {
      await axios.post('/api/auth/verify-email-code', {
        email: formData.workEmail,
        code: verifyCode,
      })
      const profileMeta = {
        signupRoleLabel: roleMeta.label,
        languages,
        oauthFlow: 'social-signup',
      }
      const res = await axios.post('/api/auth/complete-registration', {
        workEmail: formData.workEmail,
        realName: formData.realName,
        companyFullName: formData.companyShortName,
        companyShortName: formData.companyShortName,
        position: formData.position,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        role: roleMeta.backendRole,
        emailVerifyCode: verifyCode,
        oauthProvider,
        oauthCode,
        profileMeta,
      })
      if (res.data.pendingReview) {
        toast.success('Submitted for review — awaiting admin approval')
        navigate('/login')
        return
      }
      if (res.data.token) {
        localStorage.setItem('token', res.data.token)
        axios.defaults.headers.common.Authorization = `Bearer ${res.data.token}`
        toast.success('Registration successful')
        navigate(`/account-created?role=${encodeURIComponent(res.data.user?.role || roleMeta.backendRole)}`)
        return
      }
      toast.success('Registration submitted!')
      navigate('/login')
    } catch (err) {
      const msg = err.response?.data?.message || 'Registration failed'
      if (msg.toLowerCase().includes('email')) {
        setEmailError(msg)
      } else {
        toast.error(msg)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthSplitLayout
      title={
        <>
          Connect Global Capital
          <br />
          with China Assets
        </>
      }
      description="RotationStreet is the trusted AI platform bridging Chinese fund managers with global investors. Secure custody, multilingual support, end-to-end deal flow."
      footer={
        <div className="auth-footer-link">
          Already have an account? <Link to="/login">Sign in →</Link>
        </div>
      }
    >
      <AuthLogo />
      <div className="auth-subtitle">Complete your profile to get started</div>

      {oauthProvider ? (
        <div className={`auth-social-banner ${oauthProvider === 'wechat' ? 'wechat' : 'linkedin'}`}>
          {oauthProvider === 'wechat' ? <WeChatIcon /> : <LinkedInIcon />}
          <span>
            <strong>{oauthProvider === 'wechat' ? 'WeChat' : 'LinkedIn'}</strong> account connected. Complete your profile below.
          </span>
          <Link to="/login" className="auth-social-change">Change</Link>
        </div>
      ) : (
        <div className="auth-pref-box" style={{ marginBottom: 20 }}>
          <div className="auth-pref-box-desc">
            No social account linked yet.{' '}
            <Link to="/signup">Start from sign up →</Link>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="auth-form-group">
          <label className="auth-label">
            Full Name <span className="auth-sublabel">(required)</span>
          </label>
          <input
            type="text"
            className="auth-input"
            placeholder="e.g. Wang Xiaoming"
            value={formData.realName}
            onChange={(e) => updateField('realName', e.target.value)}
            required
          />
        </div>

        <div className="auth-form-row-2">
          <div className="auth-form-group">
            <label className="auth-label">
              Company Name (Short) <span className="auth-sublabel">(required)</span>
            </label>
            <input
              type="text"
              className="auth-input"
              placeholder="e.g. Sequoia China"
              value={formData.companyShortName}
              onChange={(e) => updateField('companyShortName', e.target.value)}
              required
            />
          </div>
          <div className="auth-form-group">
            <label className="auth-label">
              Job Title <span className="auth-sublabel">(required)</span>
            </label>
            <input
              type="text"
              className="auth-input"
              placeholder="e.g. Managing Partner"
              value={formData.position}
              onChange={(e) => updateField('position', e.target.value)}
              required
            />
          </div>
        </div>

        <div className="auth-form-group">
          <label className="auth-label">
            Work Email <span className="auth-sublabel">(required)</span>
          </label>
          <input
            type="email"
            className={`auth-input${emailError ? ' error' : ''}`}
            placeholder="you@company.com"
            value={formData.workEmail}
            onChange={(e) => updateField('workEmail', e.target.value)}
            required
          />
          {emailError && (
            <div className="auth-pref-box" style={{ marginTop: 8, borderColor: '#f56c6c', background: 'rgba(245,108,108,.08)' }}>
              <div className="auth-pref-box-desc" style={{ color: '#f56c6c' }}>{emailError}</div>
            </div>
          )}
        </div>

        <div className="auth-form-group">
          <label className="auth-label">
            I am a/an… <span className="auth-sublabel">(required)</span>
          </label>
          <div className="auth-role-grid auth-role-grid-4">
            {SOCIAL_ROLES.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`auth-role-card${selectedRoleId === r.id ? ' selected' : ''}`}
                onClick={() => setSelectedRoleId(r.id)}
              >
                <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center' }}>
                  <RsIcon name={ROLE_ICONS[r.id]} size="lg" />
                </div>
                <div className="auth-role-name">{r.label}</div>
                <div className="auth-role-desc">{r.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="auth-form-group">
          <label className="auth-label">Preferred Language</label>
          <LanguageMultiSelect value={languages} onChange={setLanguages} max={3} />
        </div>

        <div className="auth-form-group">
          <label className="auth-label">Email Verification Code</label>
          <input
            type="text"
            className="auth-input auth-input--code"
            placeholder="000000"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value)}
            maxLength={6}
            required
          />
          <div className="auth-resend-row">
            <span className="auth-sublabel" style={{ margin: 0 }}>Didn&apos;t receive the code?</span>
            <button
              type="button"
              className="auth-link"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={sendVerifyCode}
              disabled={countdown > 0}
            >
              {countdown > 0 ? `Resend in ${countdown}s` : 'Send code'}
            </button>
          </div>
        </div>

        <div className="auth-form-row-2">
          <div className="auth-form-group">
            <label className="auth-label">Password</label>
            <input
              type="password"
              className="auth-input"
              placeholder="Min 8 characters"
              value={formData.password}
              onChange={(e) => updateField('password', e.target.value)}
              minLength={8}
              required
            />
          </div>
          <div className="auth-form-group">
            <label className="auth-label">Confirm Password</label>
            <input
              type="password"
              className="auth-input"
              placeholder="Repeat password"
              value={formData.confirmPassword}
              onChange={(e) => updateField('confirmPassword', e.target.value)}
              required
            />
          </div>
        </div>

        <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
          {loading ? 'Submitting…' : 'Continue →'}
        </button>
      </form>
    </AuthSplitLayout>
  )
}
